import { useMoveInArray } from "@visiblethread/react-components";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import styles from "./ListInput.module.scss";
import ListInputItem from "./ListInputItem";

const ListInput = ({
  content,
  setContent,
  disabled = false,
  inputId,
  ariaLabel,
}) => {
  const [newItem, setNewItem] = useState("");
  const textareaRef = useRef(null);
  const hiddenTextRef = useRef(null);
  const [hoveredItem, setHoveredItem] = useState(-1);
  const [draggingItem, setDraggingItem] = useState(-1);
  const [draggingTarget, setDraggingTarget] = useState({
    target: -1,
    position: "",
  });
  const moveInArray = useMoveInArray;

  // Handle input change for the new item field
  const handleInputChange = (event) => {
    setNewItem(event.target.value);
    const textarea = textareaRef.current;
    const hiddenText = hiddenTextRef.current;
    if (textarea && hiddenText) {
      if (event.target.value === "") {
        textarea.style.width = "100%";
      } else {
        hiddenText.innerText = event.target.value || "";
        const { width } = hiddenText.getBoundingClientRect();
        textarea.style.width = `${width + 14}px`;
      }
    }
  };

  // Handle adding a new item when Enter is pressed
  const handleNewItem = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (newItem.trim() !== "") {
        setContent([...content, newItem]);
      }
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.width = "100%";
      }
      setNewItem("");
    }
  };

  // Handle removing an item
  const handleRemoveItem = (index) => {
    const newContent = [...content];
    newContent.splice(index, 1);
    setContent(newContent);
  };

  // Handle reordering when an item is dropped
  useEffect(() => {
    if (
      draggingTarget.target !== -1 &&
      draggingItem !== -1 &&
      draggingTarget.target !== draggingItem
    ) {
      const newContent = [...content];
      const reordered = moveInArray(
        newContent,
        draggingItem,
        draggingTarget.target
      );
      setContent(reordered);
    }
    setDraggingItem(-1);
    setDraggingTarget({ target: -1, position: "" });
  }, [draggingTarget.target]);

  return (
    <div className={styles["list-input"]}>
      <div className={styles["list-input__container"]}>
        <div
          className={styles["list-input__container__items"]}
          onMouseLeave={() => setHoveredItem(-1)}
        >
          {content.length > 0 &&
            content.map((item, index) => (
              <ListInputItem
                key={index}
                item={item}
                index={index}
                draggingItem={draggingItem}
                setDraggingItem={setDraggingItem}
                setDraggingTarget={setDraggingTarget}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                onRemove={() => handleRemoveItem(index)}
                disabled={disabled}
              />
            ))}
          {!disabled && (
            <div className={styles["list-input__input"]}>
              <textarea
                ref={textareaRef}
                className={styles["list-input__input__textarea"]}
                id={inputId}
                rows={1}
                wrap="hard"
                onChange={handleInputChange}
                onKeyDown={handleNewItem}
                value={newItem !== "" ? newItem : ""}
                placeholder="Start typing to add a new item"
                disabled={disabled}
                aria-label={ariaLabel}
              />
              <div
                ref={hiddenTextRef}
                className={styles["list-input__input__hidden-text"]}
              />
              {newItem !== "" && (
                <span className={styles["list-input__input__hint"]}>
                  Hit return to add
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ListInput.propTypes = {
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  setContent: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  inputId: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default ListInput;
